import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Assessment {
  id: string
  name: string
  category: string
  status: string
  methodology: string
  assessor: string
  risks_identified: number
  high_risks: number
  residual_risk_score: number
}

interface Risk {
  id: string
  assessment_id: string
  title: string
  category: string
  likelihood: string
  impact: string
  inherent_risk_score: number
  residual_risk_score: number
  status: string
  treatment: string
}

interface Statistics {
  total_assessments: number
  active_assessments: number
  total_risks: number
  high_risks: number
  mitigated_risks: number
  avg_inherent_risk_score: number
  avg_residual_risk_score: number
}

export default function RiskAssessmentPage() {
  const [assessments, setAssessments] = useState<Assessment[]>([])
  const [risks, setRisks] = useState<Risk[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [activeTab, setActiveTab] = useState<'assessments' | 'risks'>('assessments')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [assessmentsRes, risksRes, statsRes] = await Promise.all([
        fetch('/api/riskassessment/assessments'),
        fetch('/api/riskassessment/risks'),
        fetch('/api/riskassessment/statistics')
      ])

      if (assessmentsRes.ok) setAssessments(await assessmentsRes.json())
      if (risksRes.ok) setRisks(await risksRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching risk assessment data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadgeClass = (status: string) => {
    const classes: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800',
      in_progress: 'bg-blue-100 text-blue-800',
      review: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      archived: 'bg-gray-100 text-gray-800',
      identified: 'bg-blue-100 text-blue-800',
      analyzing: 'bg-purple-100 text-purple-800',
      mitigating: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-orange-100 text-orange-800',
      closed: 'bg-gray-100 text-gray-800'
    }
    return classes[status.toLowerCase()] || 'bg-gray-100 text-gray-800'
  }

  const getRiskScoreColor = (score: number) => {
    if (score >= 20) return 'text-red-600 bg-red-50'
    if (score >= 15) return 'text-orange-600 bg-orange-50'
    if (score >= 10) return 'text-yellow-600 bg-yellow-50'
    return 'text-green-600 bg-green-50'
  }

  const getTreatmentBadgeClass = (treatment: string) => {
    const classes: Record<string, string> = {
      mitigate: 'bg-blue-100 text-blue-800',
      transfer: 'bg-purple-100 text-purple-800',
      accept: 'bg-orange-100 text-orange-800',
      avoid: 'bg-red-100 text-red-800'
    }
    return classes[treatment.toLowerCase()] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Risk Assessment</h1>
          <p className="mt-2 text-gray-600">Identify, analyze, and manage organizational risks</p>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-7 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Assessments</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_assessments}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Active</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.active_assessments}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Risks</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.total_risks}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">High Risks</p>
              <p className="text-2xl font-bold text-red-600">{statistics.high_risks}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Mitigated</p>
              <p className="text-2xl font-bold text-green-600">{statistics.mitigated_risks}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Inherent</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.avg_inherent_risk_score}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Residual</p>
              <p className="text-2xl font-bold text-yellow-600">{statistics.avg_residual_risk_score}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('assessments')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'assessments'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Assessments ({assessments.length})
            </button>
            <button
              onClick={() => setActiveTab('risks')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'risks'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Risks ({risks.length})
            </button>
          </nav>
        </div>

        {/* Assessments Table */}
        {activeTab === 'assessments' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assessment</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Methodology</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Risks</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Residual Score</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {assessments.map((assessment) => (
                  <tr key={assessment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-sm font-medium text-gray-900">{assessment.name}</p>
                      <p className="text-sm text-gray-500">{assessment.assessor}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {assessment.category}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeClass(assessment.status)}`}>
                        {assessment.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {assessment.methodology}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{assessment.risks_identified}</span>
                      {assessment.high_risks > 0 && (
                        <span className="ml-2 px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                          {assessment.high_risks} high
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 text-sm font-bold rounded ${getRiskScoreColor(assessment.residual_risk_score)}`}>
                        {assessment.residual_risk_score.toFixed(1)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Risks Table */}
        {activeTab === 'risks' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Risk</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">L x I</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Inherent</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Residual</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Treatment</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {risks.slice(0, 20).map((risk) => (
                  <tr key={risk.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-gray-900">{risk.title}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {risk.category}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {risk.likelihood} x {risk.impact}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 text-sm font-bold rounded ${getRiskScoreColor(risk.inherent_risk_score)}`}>
                        {risk.inherent_risk_score}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 text-sm font-bold rounded ${getRiskScoreColor(risk.residual_risk_score)}`}>
                        {risk.residual_risk_score}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTreatmentBadgeClass(risk.treatment)}`}>
                        {risk.treatment}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeClass(risk.status)}`}>
                        {risk.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  )
}
